import React, { useState, useEffect, useMemo } from "react";
import { functionService } from "../../lib/functions";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { Input, Select, Spin, Pagination, Tooltip, message, Modal, Form } from "antd";
import { ExclamationCircleOutlined, SearchOutlined, PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const PAGE_SIZE = 10;

// Only admin_access === 0 (top-level admin) may change status, change
// access level, or add new users. Everyone else sees the table read-only
// and never sees the Add User button.
const isTopLevelAdmin = () => localStorage.getItem("adminAccess") === "0";

// This page now manages every access tier, not just recruiters — 0 and 3
// are confirmed (top-level admin / recruiter), 1 and 2 are a guess. Adjust
// labels/colors if your tiers mean something different.
const ACCESS_OPTIONS = [
    { value: 0, label: "Top-level Admin", bg: "#EEF0FF", color: "#4338CA", border: "#C7D0FF" },
    { value: 1, label: "Admin", bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
    { value: 2, label: "Manager", bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" },
    { value: 3, label: "Recruiter", bg: "#ECFDF5", color: "#047857", border: "#A7F3D0" },
];
const ACCESS_LABELS = ACCESS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const ACCESS_STYLE = ACCESS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o }), {});

// Fixed shared secret the backend's /signup route checks against
// process.env.SECRETINSERT. Set REACT_APP_ADMIN_SIGNUP_SECRET in your .env
// to the same value — see the note in chat about why this isn't hardcoded
// here (it would still end up visible in the built bundle either way).
const SIGNUP_SECRET = 'Coffeee@141180';

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #818CF8, #6366F1)",
    "linear-gradient(135deg, #34D399, #10B981)",
    "linear-gradient(135deg, #FBBF24, #F59E0B)",
    "linear-gradient(135deg, #F472B6, #EC4899)",
    "linear-gradient(135deg, #60A5FA, #3B82F6)",
    "linear-gradient(135deg, #A78BFA, #8B5CF6)",
];

const getInitials = (name = "") =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";

const getAvatarGradient = (id) => {
    let hash = 0;
    for (let i = 0; i < String(id).length; i++) hash = (hash * 31 + String(id).charCodeAt(i)) >>> 0;
    return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const emptyList = [];

const UserManagement = () => {
    const canManageStatus = isTopLevelAdmin();

    const [recruiters, setRecruiters] = useState(emptyList);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // No more "all" option — one of these two is always active.
    const [statusFilter, setStatusFilter] = useState("active");
    const [accessFilter, setAccessFilter] = useState("all");
    const [page, setPage] = useState(1);

    const [togglingIds, setTogglingIds] = useState(new Set());
    const [updatingAccessIds, setUpdatingAccessIds] = useState(new Set());

    const [addUserOpen, setAddUserOpen] = useState(false);
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [addUserForm] = Form.useForm();

    // Debounce the search box so filtering doesn't run on every keystroke.
    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearch(search.trim().toLowerCase());
            setPage(1);
        }, 400);
        return () => clearTimeout(handle);
    }, [search]);

    const fetchRecruiters = async () => {
        setLoading(true);
        const result = await functionService.getRecruitersActiveList();
        setLoading(false);

        if (!result.status) {
            message.error(result.error?.response?.data?.message || "Something went wrong while fetching users.");
            return;
        }

        // Exclude the logged-in admin's own row — they shouldn't see/manage
        // themselves in this list.
        const myId = localStorage.getItem("userId"); // ← swap this key if yours is named differently
        const allUsers = result.response?.data?.data || emptyList;
        setRecruiters(allUsers.filter((r) => String(r.id) !== String(myId)));
    };

    useEffect(() => {
        fetchRecruiters();
    }, []);

    // The recruiters-list endpoint returns everyone in one shot (no
    // server-side search/filter/pagination), so all filtering is client-side.
    const filteredRecruiters = useMemo(() => {
        let list = recruiters;

        if (debouncedSearch) {
            list = list.filter(
                (r) =>
                    r.name?.toLowerCase().includes(debouncedSearch) ||
                    r.email?.toLowerCase().includes(debouncedSearch)
            );
        }

        if (statusFilter !== "all") {
            const wantActive = statusFilter === "active";
            list = list.filter((r) => Boolean(r.active) === wantActive);
        }

        if (accessFilter !== "all") {
            list = list.filter((r) => r.admin_access === accessFilter);
        }

        return list;
    }, [recruiters, debouncedSearch, statusFilter, accessFilter]);

    const summary = useMemo(
        () => ({
            total: recruiters.length,
            active: recruiters.filter((r) => r.active).length,
            inactive: recruiters.filter((r) => !r.active).length,
        }),
        [recruiters]
    );

    const pagedRecruiters = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredRecruiters.slice(start, start + PAGE_SIZE);
    }, [filteredRecruiters, page]);

    const handleClearFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setStatusFilter("active");
        setAccessFilter("all");
        setPage(1);
    };

    /* ── Active/Inactive toggle: optimistic flip, revert on failure ──────── */
    const handleActiveToggle = async (recruiter) => {
        if (!canManageStatus || togglingIds.has(recruiter.id)) return;

        const flipActive = (list) =>
            list.map((r) => (r.id === recruiter.id ? { ...r, active: !r.active } : r));

        setRecruiters((prev) => flipActive(prev));
        setTogglingIds((prev) => new Set(prev).add(recruiter.id));

        const result = await functionService.toggleRecruiterActiveStatus(recruiter.id);

        setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(recruiter.id);
            return next;
        });

        if (!result.status) {
            setRecruiters((prev) => flipActive(prev)); // revert
            message.error(
                result.error?.response?.data?.message || "Could not update this user's status. Please try again.",
                4
            );
            return;
        }

        const updated = result.response?.data?.data;
        message.success(`${recruiter.name} is now ${updated?.active ? "active" : "inactive"}.`, 3);
        setRecruiters((prev) => prev.map((r) => (r.id === recruiter.id ? { ...r, ...updated } : r)));
    };

    /* ── Confirm before flipping active/inactive ──────────────────────────── */
    const confirmActiveToggle = (recruiter) => {
        if (!canManageStatus || togglingIds.has(recruiter.id)) return;

        const willDeactivate = recruiter.active;
        Modal.confirm({
            title: willDeactivate ? "Deactivate User?" : "Activate User?",
            icon: <ExclamationCircleOutlined />,
            content: willDeactivate
                ? "This user will lose access and no longer be usable in the platform. Their existing data and historical records will remain unchanged."
                : "This user will regain access to the platform.",
            okText: willDeactivate ? "Deactivate" : "Activate",
            okButtonProps: { danger: willDeactivate },
            cancelText: "Cancel",
            onOk: () => handleActiveToggle(recruiter),
            style: { marginLeft: '43%', marginRight: 0 },
        });
    };

    /* ── Access level change: optimistic set, revert on failure ──────────── */
    const handleAccessChange = async (recruiter, nextAccess) => {
        if (!canManageStatus || updatingAccessIds.has(recruiter.id) || nextAccess === recruiter.admin_access) return;

        const previousAccess = recruiter.admin_access;
        const setAccess = (list, access) =>
            list.map((r) => (r.id === recruiter.id ? { ...r, admin_access: access } : r));

        setRecruiters((prev) => setAccess(prev, nextAccess));
        setUpdatingAccessIds((prev) => new Set(prev).add(recruiter.id));

        const result = await functionService.updateRecruiterAdminAccess(recruiter.id, nextAccess);

        setUpdatingAccessIds((prev) => {
            const next = new Set(prev);
            next.delete(recruiter.id);
            return next;
        });

        if (!result.status) {
            setRecruiters((prev) => setAccess(prev, previousAccess)); // revert
            message.error(
                result.error?.response?.data?.message || "Could not update access level. Please try again.",
                4
            );
            return;
        }

        message.success(`${recruiter.name}'s access level is now ${ACCESS_LABELS[nextAccess] || nextAccess}.`, 3);
    };

    /* ── Confirm before changing access level ─────────────────────────────── */
    const confirmAccessChange = (recruiter, nextAccess) => {
        if (!canManageStatus || updatingAccessIds.has(recruiter.id) || nextAccess === recruiter.admin_access) return;

        const fromLabel = ACCESS_LABELS[recruiter.admin_access] || recruiter.admin_access;
        const toLabel = ACCESS_LABELS[nextAccess] || nextAccess;

        Modal.confirm({
            title: "Change Access Level?",
            icon: <ExclamationCircleOutlined />,
            content: `${recruiter.name}'s access level will change from "${fromLabel}" to "${toLabel}". This affects what they can see and do in the admin dashboard.`,
            okText: "Change Access",
            cancelText: "Cancel",
            onOk: () => handleAccessChange(recruiter, nextAccess),
            style: { marginLeft: '43%', marginRight: 0 },
        });
    };

    /* ── Add User ──────────────────────────────────────────────────────────
       Backend's /signup route returns 200 for BOTH "Registered" and
       "Already Registered" (the latter meaning no new row was created), and
       returns HTTP 400 for the "Auth Failed" case — which axios routes into
       .catch() automatically, so that path is handled there. */
    const handleAddUser = async () => {
        const values = await addUserForm.validateFields().catch(() => null);
        if (!values) return;

        setAddUserLoading(true);
        const result = await functionService.adminSignup({
            email: values.email.trim(),
            password: values.password,
            name: values.name.trim(),
            access: values.access,
            inter: SIGNUP_SECRET,
        });
        setAddUserLoading(false);

        if (!result.status) {
            message.error(result.error?.response?.data?.meta?.message || "Could not create this user. Please try again.", 4);
            return;
        }

        const serverMessage = result.response?.data?.meta?.message;

        if (serverMessage === "Already Registered") {
            message.warning("A user with this email is already registered.", 4);
            return;
        }

        message.success(`${values.name} was added as ${ACCESS_LABELS[values.access] || values.access}.`, 3);
        setAddUserOpen(false);
        addUserForm.resetFields();
        fetchRecruiters(); // backend doesn't return the new row, so re-fetch
    };

    const closeAddUser = () => {
        if (addUserLoading) return;
        setAddUserOpen(false);
        addUserForm.resetFields();
    };

    const hasActiveFilters = Boolean(search) || statusFilter !== "active" || accessFilter !== "all";

    const summaryCards = [
        { label: "Total Users", val: summary.total, color: "#4F46E5", light: "#EEF2FF" },
        { label: "Active Users", val: summary.active, color: "#10B981", light: "#D1FAE5" },
        { label: "Inactive Users", val: summary.inactive, color: "#64748B", light: "#F1F5F9" },
    ];

    if (!canManageStatus) {
        return <div>You don't have access to this page.</div>;
    }

    return (
        <Styled.RecruiterMainDiv>
            <Sidemenu />
            <Styled.RecruiterContentWrapDiv>
                <Styled.RecruiterTopContainer>
                    <div className="left">
                        <div className="page-title">User Management</div>
                        <div className="page-subtitle">
                            Manage user availability and access level across the platform. Deactivating a user
                            preserves all historical data while revoking their access.
                        </div>
                    </div>
                </Styled.RecruiterTopContainer>

                <Styled.SummaryGrid>
                    {summaryCards.map((card, idx) => (
                        <Styled.SummaryCard key={card.label} color={card.color} light={card.light} idx={idx}>
                            <Styled.SummaryValue color={card.color}>{loading && !recruiters.length ? "—" : card.val}</Styled.SummaryValue>
                            <Styled.SummaryLabel>{card.label}</Styled.SummaryLabel>
                        </Styled.SummaryCard>
                    ))}
                </Styled.SummaryGrid>
                <Styled.FilterRow>
                    <Styled.StyledSearchWrap>
                        <Input
                            placeholder="Search user name or email"
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                            style={{ width: 280 }}
                        />
                    </Styled.StyledSearchWrap>

                    <Styled.StyledSelectWrap>
                        <Select
                            value={accessFilter}
                            onChange={(value) => {
                                setAccessFilter(value);
                                setPage(1);
                            }}
                            style={{ width: 190 }}
                        >
                            <Option value="all">All Access Levels</Option>
                            {ACCESS_OPTIONS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </Styled.StyledSelectWrap>

                    <Styled.ToggleGroup>
                        <Styled.ToggleButton
                            type="button"
                            active={statusFilter === "active"}
                            onClick={() => {
                                setStatusFilter("active");
                                setPage(1);
                            }}
                            onDoubleClick={() => {
                                setStatusFilter("all");
                                setPage(1);
                            }}
                        >
                            <span className="dot" />
                            Active
                        </Styled.ToggleButton>
                        <Styled.ToggleButton
                            type="button"
                            $variant="inactive"
                            active={statusFilter === "inactive"}
                            onClick={() => {
                                setStatusFilter("inactive");
                                setPage(1);
                            }}
                            onDoubleClick={() => {
                                setStatusFilter("all");
                                setPage(1);
                            }}
                        >
                            <span className="dot" />
                            Inactive
                        </Styled.ToggleButton>
                    </Styled.ToggleGroup>


                    {hasActiveFilters && (
                        <Styled.ClearFiltersButton onClick={handleClearFilters}>Clear Filters</Styled.ClearFiltersButton>
                    )}

                    <Styled.AddUserButton type="button" onClick={() => setAddUserOpen(true)} style={{ marginLeft: "auto" }}>
                        <PlusOutlined />
                        Add User
                    </Styled.AddUserButton>
                </Styled.FilterRow>

                <Styled.TableScrollArea>
                    {loading ? (
                        <Styled.SpinWrap>
                            <Spin size="large" />
                        </Styled.SpinWrap>
                    ) : pagedRecruiters.length === 0 ? (
                        <Styled.EmptyWrap>
                            <div className="icon">🗂️</div>
                            <div>No users found</div>
                            {hasActiveFilters && (
                                <Styled.ClearFiltersButton onClick={handleClearFilters}>Clear Filters</Styled.ClearFiltersButton>
                            )}
                        </Styled.EmptyWrap>
                    ) : (
                        <Styled.RecruiterMainTable>
                            <thead>
                                <tr>
                                    <Styled.THMeta left style={{ width: "24%" }}>Name</Styled.THMeta>
                                    <Styled.THMeta left style={{ width: "28%" }}>Email</Styled.THMeta>
                                    <Styled.THMeta style={{ width: "21%" }}>Access Level</Styled.THMeta>
                                    <Styled.THMeta style={{ width: "16%" }}>Status</Styled.THMeta>
                                    <Styled.THMeta style={{ width: "14%" }}>Created</Styled.THMeta>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecruiters.map((recruiter) => {
                                    const accessStyle = ACCESS_STYLE[recruiter.admin_access] || {};
                                    return (
                                        <Styled.DataRow key={recruiter.id} data-inactive={!recruiter.active}>
                                            <td style={{ textAlign: "left" }}>
                                                <Styled.RecruiterCell>
                                                    <Styled.RecruiterAvatar bg={getAvatarGradient(recruiter.id)}>
                                                        {getInitials(recruiter.name)}
                                                    </Styled.RecruiterAvatar>
                                                    <span className="name">{recruiter.name}</span>
                                                </Styled.RecruiterCell>
                                            </td>
                                            <td style={{ textAlign: "left" }}>
                                                <Styled.EmailText title={recruiter.email}>{recruiter.email}</Styled.EmailText>
                                            </td>
                                            <td>
                                                <Styled.AccessPillWrap
                                                    $bg={accessStyle.bg}
                                                    $color={accessStyle.color}
                                                    $border={accessStyle.border}
                                                >
                                                    <Select
                                                        value={recruiter.admin_access}
                                                        onChange={(value) => confirmAccessChange(recruiter, value)}
                                                        loading={updatingAccessIds.has(recruiter.id)}
                                                        disabled={updatingAccessIds.has(recruiter.id)}
                                                        size="small"
                                                        popupMatchSelectWidth={false}
                                                    >
                                                        {ACCESS_OPTIONS.map((opt) => (
                                                            <Option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Styled.AccessPillWrap>
                                            </td>
                                            <td>
                                                <Styled.StatusControl>
                                                    <Styled.StatusPill active={!!recruiter.active}>
                                                        <span className="dot" />
                                                        {recruiter.active ? "Active" : "Inactive"}
                                                    </Styled.StatusPill>
                                                    <Tooltip title={recruiter.active ? "Mark as Inactive" : "Mark as Active"}>
                                                        <Styled.StatusToggleTrack
                                                            active={!!recruiter.active}
                                                            $loading={togglingIds.has(recruiter.id)}
                                                            onClick={() => confirmActiveToggle(recruiter)}
                                                            role="switch"
                                                            aria-checked={!!recruiter.active}
                                                        >
                                                            <Styled.StatusToggleThumb active={!!recruiter.active} />
                                                        </Styled.StatusToggleTrack>
                                                    </Tooltip>
                                                </Styled.StatusControl>
                                            </td>
                                            <td>
                                                <Styled.DateText>{formatDate(recruiter.created_at)}</Styled.DateText>
                                            </td>
                                        </Styled.DataRow>
                                    );
                                })}
                            </tbody>
                        </Styled.RecruiterMainTable>
                    )}
                </Styled.TableScrollArea>

                {/* {filteredRecruiters.length > PAGE_SIZE && (
                    <Styled.PaginationRow>
                        <Pagination
                            current={page}
                            pageSize={PAGE_SIZE}
                            total={filteredRecruiters.length}
                            onChange={setPage}
                            showSizeChanger={false}
                            style={{ marginBottom: "-8px" }}
                        />
                    </Styled.PaginationRow>
                )} */}

                <Modal
                    open={addUserOpen}
                    title="Add User"
                    onCancel={closeAddUser}
                    onOk={handleAddUser}
                    okText="Add User"
                    confirmLoading={addUserLoading}
                    cancelButtonProps={{ disabled: addUserLoading }}
                    maskClosable={!addUserLoading}
                    closable={!addUserLoading}
                    destroyOnClose
                    style={{ marginLeft: '41%', marginRight: 0 }}
                >
                    <Styled.StyledFormWrap>

                        <Form form={addUserForm} layout="vertical" requiredMark={false}>
                            <Form.Item
                                name="name"
                                label="Full Name"
                                rules={[{ required: true, message: "Name is required" }]}
                            >
                                <Input placeholder="e.g. Aarti Pabnani" />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: "Email is required" },
                                    { type: "email", message: "Enter a valid email address" },
                                ]}
                            >
                                <Input placeholder="e.g. aarti.p@coffeee.io" />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[
                                    { required: true, message: "Password is required" },
                                    { min: 6, message: "Password must be at least 6 characters" },
                                ]}
                            >
                                <Input.Password placeholder="Set an initial password" />
                            </Form.Item>
                            <Form.Item
                                name="confirmPassword"
                                label="Confirm Password"
                                dependencies={["password"]}
                                rules={[
                                    { required: true, message: "Please confirm the password" },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue("password") === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error("Passwords do not match"));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Re-enter the password" autoComplete="new-password" />
                            </Form.Item>

                            <Form.Item
                                name="access"
                                label="Access Level"
                                rules={[{ required: true, message: "Access level is required" }]}
                            >
                                <Select placeholder="Select access level">
                                    {ACCESS_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Form>
                    </Styled.StyledFormWrap>

                </Modal>
            </Styled.RecruiterContentWrapDiv>
        </Styled.RecruiterMainDiv>
    );
};

export default UserManagement;